import { FC } from "react";
import { LuX } from "react-icons/lu";
import { useSelector } from "react-redux";

import {
  DRAWER_HEIGHT,
  DRAWER_UNITS,
  DrawerSnapProps,
} from "@constants/Drawer";
import { useDrawer } from "@hooks/useDrawer/useDrawer";
import { RootState } from "@store/index";

const DrawerSnap: FC<DrawerSnapProps> = ({
  children,
  direction = "bottom",
  isOpen,
  onClose,
  useSnapPoints = true,
}) => {
  if (direction !== "bottom") {
    throw new Error("Only bottom direction is supported");
  }

  const openPercent = useSelector(
    (state: RootState) => state.drawer.openPercent
  );

  const { drawerContentRef, isDragging, startDrag } = useDrawer({
    isOpen,
    onClose,
    useSnapPoints,
  });

  const translateY = DRAWER_HEIGHT.FULL - openPercent;
  const heightUnit = CSS.supports("height", "100dvh")
    ? DRAWER_UNITS.UNIT_DVH
    : DRAWER_UNITS.UNIT_VH;

  return (
    <div
      className={`fg-drawer-container fg-drawer-shadow bg-white ${isDragging ? "dragging" : ""}`}
      style={{ transform: `translate3d(0, ${translateY}vh, 0)` }}
    >
      {/* Handle */}
      <button
        type="button"
        aria-label="Drag drawer"
        className="fg-drawer-handle d-flex justify-content-center align-items-center py-3 w-100 bg-transparent"
        onMouseDown={e => startDrag(e?.clientY || DRAWER_HEIGHT.ZERO)}
        onTouchStart={e =>
          startDrag(e?.touches[0]?.clientY || DRAWER_HEIGHT.ZERO)
        }
      >
        <span className="fg-drawer-handle-bar rounded-pill" />
      </button>

      <button
        onClick={onClose}
        className="fg-drawer-close-btn btn btn-light rounded-circle"
      >
        <LuX size={22} aria-hidden="true" />
      </button>

      {/* Content */}
      <div
        ref={el => {
          drawerContentRef.current = el;
        }}
        className="fg-drawer-content p-3 overflow-auto"
        style={{
          maxHeight: `calc(${openPercent}${heightUnit} - var(--drawer-footer-height))`,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default DrawerSnap;
